import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './WrapLayout.css';
import { Instance }  from 'cdux_common';
import { CloseInstanceButton } from 'cdux_common';
import { Fallback } from 'cdux_common';
import { DownloadButton } from 'cdux_common';
import { useSession } from '../../Utils/SessionProvider';
import { checkReadOnly } from '../../Utils/ReadOnlyElements';
import { useTranslate } from 'react-translate';
import { InstanceBuilder, Transform } from '../../models/Instance';

export function WrapLayout(props) {

    const [rootInstances, setRootInstances] = useState(props.instances);
    const [instances, setInstances] = useState([]);
    const [animationDirection, setAnimationDirection] = useState('right');
    //const [isFirstRender, setIsFirstRender] = useState(false);

    const scrollX = useRef(null);
    const scrollY = useRef(null);

    const session = useSession();
    const isReadOnly = checkReadOnly();
    const t = useTranslate("viewer");

    const loading = {
        width: 500,
        height: 250
    }

    const variants = {
        enter: (direction) => ({
            x: direction === 'right' ? '100%' : '-100%',
            opacity: 0
        }),
        center: {
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            x: direction === 'right' ? '-100%' : '100%',
            opacity: 0
        }),
    };

    function getLoadingMessage(instance) {
        switch (instance.itemType) {
            case "pdf":
                return t('loadPdf');
            default: return t('loadDocument');

        }
    }

    function getScrollPosition() {
        scrollX.current = window.scrollX;
        scrollY.current = window.scrollY;
    }


    const lastInstance = useMemo(() => {
        if (instances.length === 0) return null;
        return instances[instances.length - 1];
    }, [instances]);

    function pushInstance(newInstance, direction) {    
        setInstances(prev => {

            if (rootInstances.some((instance) => instance.id === newInstance.id)) {
                const instanceElement = document.getElementById(newInstance.id);
                instanceElement.scrollIntoView({ behavior: 'smooth' });
                return [...prev];
            } else {
                return [...prev, newInstance]
            }
           
        });
        setAnimationDirection(direction);
    }

    function handleAnnotation(args) {
        let section;
        if (args.sections) {
            section = args.sections.find((section) => section.Pages.includes(args.destination - 1))

            if (section) {
                const parentSection = args.sections.find((section) => section.Pages[0] === 0);
                const instance = rootInstances.find(instance => instance.id === parentSection.Id);
                openInstance(section.Id, instance)
            }

        } else if (args.annotation.fieldName) {
            const cleanedLinkedInstance = args.annotation.fieldName.replace(/^cdux-/, '');
            const instances = [
                ...session.instances,
                ...(session.linkedInstances ?? [])
            ];
            const instance = instances.find(instance => instance.id === cleanedLinkedInstance);

            if (instance) {
                openInstance(instance.id, instance);
            }
        }
    }

    function openInstance(id, instance) {
     

        const transform = new Transform(
            0,
            0,
            window.innerWidth * 0.5,
            window.innerHeight * 0.5,
            0.5
        )

        const newLinkedInstance = new InstanceBuilder()
            .setId(id)
            .setCanBeEmbedded(instance.canBeEmbedded)
            .setChildrenOverflow(instance.childrenOverflow)
            .setCurrentPageId(instance.currentPageId)
            .setfileName(instance.fileName)
            .setHasRoundedCorner(instance.hasRoundedCorner)
            .setInteractivePages(instance.interactivePages)
            .setItemType(instance.itemType)
            .setNaturalOrder(instance.naturalOrder)
            .setPages(instance.pages)
            .setPosition(instance.position)
            .setpublicWebUrl(instance.publicWebUrl)
            .setReferenceData(instance.referenceData)
            .setThumbnailSize(instance.thumbnailSize)
            .setThumbnailUrl(instance.thumbnailUrl)
            .setTransform(transform)
            .build();

        if (!scrollY.current && !scrollX.current) {
            getScrollPosition();
        }
        
        pushInstance(newLinkedInstance, 'right')
    }

    function closeInstance() {
        setAnimationDirection('left');
        setInstances(prevList => prevList.slice(0, -1));
    }

    return (<div className="wrapGrid" style={props.style}>
        <AnimatePresence
            initial={false}
            custom={animationDirection}
            mode={instances.length > 1 ? 'sync' : 'wait'}        
        >

            {instances.length === 0 && rootInstances
            .sort((i,j) => i.naturalOrder - j.naturalOrder)
                .map((i, index) => {
                    return (
                        <motion.div
                            key={i.id}
                            custom={animationDirection}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.3 }}
                            style={{
                                width: '100%',
                                height: '100%',
                                zIndex: animationDirection === 'right' ? 2 : 1,
                            }}
                            onAnimationComplete={(definition) => {
                                if (definition === "center" && scrollY.current) {
                                    window.scrollTo({
                                        top: scrollY.current,
                                        left: scrollX.current,
                                        behavior: "instant"
                                    });
                                    scrollY.current = null;
                                    scrollX.current = null;
                                } else if (definition === "exit") {
                                    const top = document.getElementById('titleBox').getBoundingClientRect().height;
                                    window.scrollTo({
                                        top: top ?? 0,
                                        left: 0,
                                        behavior: 'instant'
                                    })
                                }
                            }}

                        >
                            <Instance
                                key={'wrap-thumbnail-' + i.id}
                                index={index}
                                instance={i}
                                url={i.publicWebUrl}
                                workspace={session.workspace}
                                theme={session.theme}
                                isReadOnly={isReadOnly}
                                canBeMoved={false}
                                handleAnnotation={handleAnnotation}
                                buttons={{
                                    mainAction: 'mainAction',
                                    download: <DownloadButton instance={i} target={i.publicWebUrl} onClick={props.downloadFile} theme={session.theme} />
                                }}
                                Fallback={<Fallback
                                    workspace={session.workspace}
                                    theme={session.theme}
                                    thumbnailUrl={i.thumbnailUrl}
                                    message={i.itemType === "web" ? t('iframeFallbackMessage') : t('unsupported-message')}
                                    button={<a className="button" href={i.publicWebUrl} target="_blank" rel="noreferrer" download>{i.itemType === "web" ? t('iframeFallbackBtn') : t('download-label')}</a>}
                                />}
                                loading={loading }
                                loadingMessage={getLoadingMessage(i) }
                                appearDelay={true }
                                useThumbnail={true }
                            />
                        </motion.div>
                    )                    
                }              
            )
            }    
            {instances.length > 0 && lastInstance && (() => {
                return (
                    <motion.div
                        key={`instance-${lastInstance.id}`}
                        custom={animationDirection}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.3 }}
                        style={{
                            gridRow: 1,
                            gridColumn: 1,
                            width: '100%',
                            height: '100%',
                            zIndex: animationDirection === 'right' ? 2 : 1, 
                        }}
      
                    >
                        <Instance
                            key={'wrap-thumbnail-' + lastInstance.id}
                            instance={lastInstance}
                            url={lastInstance.publicWebUrl}
                            workspace={session.workspace}
                            theme={session.theme}
                            isReadOnly={isReadOnly}
                            canBeMoved={false}
                            handleAnnotation={handleAnnotation}
                            closeInstance={closeInstance}
                            buttons={{
                                close: < CloseInstanceButton theme={session.theme} onClick={() => closeInstance(lastInstance)} />,
                                mainAction: 'mainAction',
                                download: <DownloadButton instance={lastInstance} target={lastInstance.publicWebUrl} onClick={props.downloadFile} theme={session.theme} />
                            }}
                            Fallback={<Fallback
                                workspace={session.workspace}
                                theme={session.theme}
                                thumbnailUrl={lastInstance.thumbnailUrl}
                                message={lastInstance.itemType === "web" ? t('iframeFallbackMessage') : t('unsupported-message')}
                                button={<a className="button" href={lastInstance.publicWebUrl} target="_blank" rel="noreferrer" download>{lastInstance.itemType === "web" ? t('iframeFallbackBtn') : t('download-label')}</a>}
                            />}
                            loading={loading}
                            loadingMessage={getLoadingMessage(lastInstance)}
                        />
                    </motion.div>
                );
            })()}
        </AnimatePresence>
    </div>);
}